import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import productUsageService from '../../services/productUsageService';

const formatDate = (value) => (value ? String(value).slice(0, 10) : '');

const addressLine = (school) => {
  const uploaded = formatDate(school.last_uploaded_at);
  const when = uploaded ? `, uploaded ${uploaded}` : '';
  return `${school.school_name} (${school.address_count.toLocaleString()} addresses${when})`;
};

const enrollmentLine = (school) => (
  `${school.school_name} (${(school.years || []).join(', ')})`
);

const savedLine = (school) => (
  school.saved_by_user ? `${school.school_name} (saved by this user)` : school.school_name
);

const targetLine = (school) => {
  const years = (school.years || []).join(', ');
  const saved = school.saved_by_user ? 'saved by this user' : '';
  const detail = [years, saved].filter(Boolean).join('; ');
  return detail ? `${school.school_name} (${detail})` : school.school_name;
};

const Lines = ({ items, render }) => {
  if (!items?.length) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
      {items.map(render).join('\n')}
    </Typography>
  );
};

const matchesQuery = (user, query) => {
  if (!query) return true;
  const haystack = [
    user.name,
    user.email,
    ...(user.teams || []),
    ...(user.student_addresses || []).map((school) => school.school_name),
    ...(user.enrollment || []).map((school) => school.school_name),
    ...(user.grade_caps || []).map((school) => school.school_name),
    ...(user.targets || []).map((school) => school.school_name),
  ].join(' ').toLowerCase();
  return haystack.includes(query);
};

const DataUploads = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [excludeAdmins, setExcludeAdmins] = useState(true);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [query, setQuery] = useState('');

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productUsageService.getDataUploads({ excludeAdmins });
      setReport(data);
    } catch (error) {
      console.error('Data uploads error:', error);
      enqueueSnackbar(error.message || 'Failed to load data uploads', { variant: 'error' });
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [excludeAdmins, enqueueSnackbar]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const download = useCallback(async () => {
    setDownloading(true);
    try {
      await productUsageService.downloadDataUploads({ excludeAdmins });
    } catch (error) {
      console.error('Data uploads download error:', error);
      enqueueSnackbar(error.message || 'Download failed', { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  }, [excludeAdmins, enqueueSnackbar]);

  const summary = report?.summary;
  const needle = query.trim().toLowerCase();
  const users = useMemo(
    () => (report?.users || []).filter((user) => matchesQuery(user, needle)),
    [report, needle],
  );

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Search users, teams, or schools"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ minWidth: 280 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={excludeAdmins}
                onChange={(event) => setExcludeAdmins(event.target.checked)}
                size="small"
              />
            }
            label="Exclude admin users"
          />

          <Box sx={{ flexGrow: 1 }} />

          <Button
            startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={download}
            disabled={!report || downloading}
            variant="contained"
          >
            Export CSV
          </Button>
        </Stack>

        {summary && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color="primary"
              label={`${summary.schools_with_addresses.toLocaleString()} schools with student addresses`}
            />
            <Chip
              size="small"
              label={`${summary.address_rows.toLocaleString()} address rows`}
            />
            <Chip
              size="small"
              label={`${summary.schools_with_enrollment.toLocaleString()} schools with new enrollment`}
            />
            <Chip
              size="small"
              label={`${summary.schools_with_caps.toLocaleString()} schools with grade caps`}
            />
            <Chip
              size="small"
              label={`${summary.schools_with_targets.toLocaleString()} schools with targets`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${summary.users_with_any.toLocaleString()} users with any upload`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${summary.unassigned_schools.toLocaleString()} unassigned schools`}
            />
          </Stack>
        )}
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {report?.attribution_note && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {report.attribution_note} This report uses the database this app is connected to.
        </Alert>
      )}

      {report && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>Users with uploaded data</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {needle
              ? `${users.length.toLocaleString()} of ${report.users.length.toLocaleString()} users match.`
              : `${report.users.length.toLocaleString()} users. A user is included when a school on their team has data, or when they saved grade caps or targets.`}
          </Typography>
          <TableContainer sx={{ maxHeight: 640 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Teams</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Student addresses</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>New enrollment</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Grade caps</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Targets</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.uid} hover>
                    <TableCell sx={{ verticalAlign: 'top', minWidth: 180 }}>
                      <Typography variant="body2">{user.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'pre-line' }}>
                      {(user.teams || []).join('\n') || '—'}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Lines items={user.student_addresses} render={addressLine} />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Lines items={user.enrollment} render={enrollmentLine} />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Lines items={user.grade_caps} render={savedLine} />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Lines items={user.targets} render={targetLine} />
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No users with uploaded data{needle ? ' match this search' : ''}.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {report?.unassigned_schools?.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Schools with data and no active team</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These schools have uploads, but no active team assignment, so they are not listed under a user.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>School</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Student addresses</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>New enrollment</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Grade caps</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Targets</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.unassigned_schools.map((school) => (
                  <TableRow key={school.school_uuid} hover>
                    <TableCell>{school.school_name || school.school_uuid}</TableCell>
                    <TableCell>
                      {school.address_count
                        ? `${school.address_count.toLocaleString()} (${formatDate(school.last_uploaded_at)})`
                        : '—'}
                    </TableCell>
                    <TableCell>{(school.enrollment_years || []).join(', ') || '—'}</TableCell>
                    <TableCell>{school.has_caps ? 'Yes' : '—'}</TableCell>
                    <TableCell>{(school.target_years || []).join(', ') || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </>
  );
};

export default DataUploads;
